import { create } from 'zustand';

import type { WorkflowMutationEntry } from '@/components/chat';

/**
 * Applies a batch of agent-proposed mutations to the mounted canvas.
 * Returns false when nothing landed (blocked by the editing lock, or every
 * mutation in the batch failed), so the caller knows not to mark the
 * proposal as applied.
 */
export type CanvasApplyMutationsHandler = (
  mutations: WorkflowMutationEntry[],
  applyKey?: string,
) => boolean;

export interface CanvasApplyMutationsBridge {
  apply: CanvasApplyMutationsHandler;
  disabled: boolean;
  disabledReason?: string;
  /**
   * Identity of the workflow the mounted canvas currently shows: the real
   * id once saved, or the same `new:<createdAt>` draft key the chat side
   * freezes an unsaved proposal's target as (see onMutation in
   * use-agent-chat.ts) -- never a bare null, or the mismatch check below
   * would let a proposal for a different workflow land on any blank canvas.
   */
  workflowId: string | null;
}

interface CanvasApplyMutationsBridgeState {
  /** The embedded canvas's apply handler, or null when no canvas is mounted. */
  bridge: CanvasApplyMutationsBridge | null;
  setBridge: (bridge: CanvasApplyMutationsBridge | null) => void;
}

/**
 * Connects a chat rail to the workflow canvas mounted beside it, mirroring
 * `canvas-run-bridge-store`.
 *
 * The A2A Apply card only *asks* the frontend to land a batch of mutations —
 * the canvas owns applying them, and with it the editing lock, the undo
 * snapshot, and the auto-layout pass. On the `/workflow/:id` route the chat
 * lives inside the editor and gets `applyBufferedMutations` as a prop. In a
 * surface host the chat is the editor's sibling, so no prop path exists: the
 * canvas publishes its handler (and whether it is locked) here, and the
 * host's chat calls it.
 */
export const useCanvasApplyMutationsBridgeStore =
  create<CanvasApplyMutationsBridgeState>((set) => ({
    bridge: null,
    setBridge: (bridge) => set({ bridge }),
  }));

/**
 * Apply a batch of mutations on the mounted canvas, or refuse when none is
 * listening. Reads the bridge at call time so the returned function is
 * stable across renders.
 *
 * `targetWorkflowId`, when given, must match the mounted canvas's workflow --
 * the caller's Apply button is already disabled on a mismatch, but a
 * proposal for one workflow reaching a canvas now showing another (the user
 * switched panes since the card was shown) would otherwise apply mutations
 * keyed to instance ids that don't exist on the graph on screen.
 */
export function dispatchCanvasApplyMutations(
  mutations: WorkflowMutationEntry[],
  targetWorkflowId?: string | null,
  applyKey?: string,
): boolean {
  const bridge = useCanvasApplyMutationsBridgeStore.getState().bridge;
  if (!bridge) return false;
  if (
    targetWorkflowId != null &&
    bridge.workflowId != null &&
    targetWorkflowId !== bridge.workflowId
  ) {
    return false;
  }
  return bridge.apply(mutations, applyKey);
}
